// Datadog-style drag-to-zoom: drag across bars to select a time range; on release the
// page reloads with created_at_start/created_at_end covering the selected buckets, so
// the server re-buckets the narrower window on a finer ladder rung. Selection is drawn
// as a translucent overlay while dragging and snaps outward to whole buckets.
export function dragZoomPlugin(intervalMs, highlightColor) {
    const drag = { startPx: null, currentPx: null }
    let removeListeners = null

    const selectionBounds = (chart) => {
        const { left, right } = chart.chartArea
        return {
            from: Math.max(Math.min(drag.startPx, drag.currentPx), left),
            to: Math.min(Math.max(drag.startPx, drag.currentPx), right),
        }
    }

    const zoomTo = (chart) => {
        const { from, to } = selectionBounds(chart)
        if (to - from < 6) {
            return // a click, not a selection
        }

        // Snap outward to whole buckets, anchored on the rendered bucket grid.
        const anchor = chart.data.datasets[0]?.data[0]?.x
        const snap = (ms, roundFn) => {
            return anchor === undefined
                ? ms
                : anchor + roundFn((ms - anchor) / intervalMs) * intervalMs
        }

        const startMs = snap(chart.scales.x.getValueForPixel(from), Math.floor)
        const endMs = snap(chart.scales.x.getValueForPixel(to), Math.ceil)

        if (endMs - startMs < 60 * 1000) {
            return // 1 minute is the smallest zoomable window
        }

        const url = new URL(window.location.href)
        url.searchParams.delete('created_at') // an explicit range replaces a sliding preset token
        url.searchParams.set('created_at_start', new Date(startMs).toISOString())
        url.searchParams.set('created_at_end', new Date(endMs).toISOString())
        window.Turbo ? window.Turbo.visit(url.toString()) : window.location.assign(url.toString())
    }

    return {
        id: 'timeAxisDragZoom',
        afterInit(chart) {
            const canvas = chart.canvas
            canvas.style.cursor = 'crosshair'
            const pixelX = (event) => {
                return event.clientX - canvas.getBoundingClientRect().left
            }

            const onMouseDown = (event) => {
                const x = pixelX(event)
                if (event.button !== 0 || x < chart.chartArea.left || x > chart.chartArea.right) {
                    return
                }
                event.preventDefault() // no text selection while dragging
                drag.startPx = x
                drag.currentPx = x
            }
            const onMouseMove = (event) => {
                if (drag.startPx === null) {
                    return
                }
                drag.currentPx = pixelX(event)
                chart.update('none')
            }
            const onMouseUp = () => {
                if (drag.startPx === null) {
                    return
                }
                zoomTo(chart)
                drag.startPx = null
                drag.currentPx = null
                chart.update('none')
            }

            canvas.addEventListener('mousedown', onMouseDown)
            // Window-level move/up so a drag keeps tracking outside the canvas.
            window.addEventListener('mousemove', onMouseMove)
            window.addEventListener('mouseup', onMouseUp)
            removeListeners = () => {
                canvas.removeEventListener('mousedown', onMouseDown)
                window.removeEventListener('mousemove', onMouseMove)
                window.removeEventListener('mouseup', onMouseUp)
            }
        },
        afterDestroy() {
            if (removeListeners) {
                removeListeners()
            }
        },
        afterDraw(chart) {
            if (drag.startPx === null) {
                return
            }

            const { from, to } = selectionBounds(chart)
            if (to <= from) {
                return
            }

            const { top, bottom } = chart.chartArea
            const ctx = chart.ctx
            ctx.save()
            ctx.globalAlpha = 0.15
            ctx.fillStyle = highlightColor
            ctx.fillRect(from, top, to - from, bottom - top)
            ctx.globalAlpha = 0.6
            ctx.strokeStyle = highlightColor
            ctx.strokeRect(from, top, to - from, bottom - top)
            ctx.restore()
        },
    }
}
